// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { buffer, hex, utf8 } from './convert.js';
import { Entropy } from './entropy.js';
const { subtle } = globalThis.crypto;
const storage = globalThis.sessionStorage;
const ERR_MSG = 'Failed to store item in Safe';
export class Safe {
    async put(name, passkey, data) {
        if (storage.getItem(name)) {
            throw new Error(ERR_MSG);
        }
        return this.overwrite(name, passkey, data);
    }
    async overwrite(name, passkey, data) {
        if (this.#isNotValid(name, passkey, data)) {
            throw new Error(ERR_MSG);
        }
        const iv = new Entropy();
        if (typeof passkey === 'string') {
            try {
                passkey = await subtle.importKey('raw', utf8.toBytes(passkey), 'PBKDF2', false, ['deriveBits', 'deriveKey']);
                passkey = await subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-512', salt: iv.bytes, iterations: 210000 }, passkey, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
            }
            catch (err) {
                throw new Error(ERR_MSG);
            }
        }
        try {
            if (typeof data === 'bigint') {
                data = data.toString();
            }
            data = JSON.stringify(data);
            const encoded = utf8.toBytes(data);
            const encrypted = await subtle.encrypt({ name: 'AES-GCM', iv: iv.buffer }, passkey, encoded);
            const record = {
                encrypted: buffer.toHex(encrypted),
                iv: iv.hex
            };
            await new Promise((resolve, reject) => {
                try {
                    storage.setItem(name, JSON.stringify(record));
                    resolve();
                }
                catch (err) {
                    reject(err);
                }
            });
            passkey = '';
        }
        catch (err) {
            throw new Error(ERR_MSG);
        }
        return (storage.getItem(name) != null);
    }
    async get(name, passkey) {
        if (this.#isNotValid(name, passkey)) {
            return null;
        }
        const item = await new Promise(resolve => {
            resolve(storage.getItem(name));
        });
        if (item == null) {
            return null;
        }
        const record = JSON.parse(item);
        const encrypted = hex.toBytes(record.encrypted);
        const iv = new Entropy(record.iv);
        try {
            if (typeof passkey === 'string') {
                passkey = await subtle.importKey('raw', utf8.toBytes(passkey), 'PBKDF2', false, ['deriveBits', 'deriveKey']);
                passkey = await subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-512', salt: iv.bytes, iterations: 210000 }, passkey, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
            }
        }
        catch (err) {
            return null;
        }
        try {
            const decrypted = await subtle.decrypt({ name: 'AES-GCM', iv: iv.buffer }, passkey, encrypted);
            const decoded = buffer.toUtf8(decrypted);
            const data = JSON.parse(decoded);
            passkey = '';
            storage.removeItem(name);
            return data;
        }
        catch (err) {
            return null;
        }
    }
    #isNotValid(name, passkey, data) {
        if (typeof name !== 'string' || name === '') {
            return true;
        }
        if (typeof passkey !== 'string' || passkey === '') {
            if (!(passkey instanceof CryptoKey)) {
                return true;
            }
        }
        if (typeof data === 'object') {
            try {
                JSON.stringify(data);
            }
            catch (err) {
                return true;
            }
        }
        return false;
    }
}
